import * as React from 'react';
import { cn } from '@/lib/utils';

interface StepBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Total number of steps
   */
  totalSteps: number;
  /**
   * Current active step (1-indexed)
   */
  currentStep: number;
  /**
   * Display variant
   * - 'steps': Individual separated steps with gaps
   * - 'progress': Single continuous progress bar
   */
  variant?: 'steps' | 'progress';
  /**
   * Rounded corners variant for the container
   */
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  /**
   * Rounded corners variant for the right side of the active bar (progress variant only)
   */
  activeRoundedRight?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  /**
   * Whether the bar background should be transparent
   */
  transparent?: boolean;
  /**
   * Height of the bar
   */
  height?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string;
  /**
   * Custom class for the active step color
   */
  activeClassName?: string;
  /**
   * Custom class for the inactive step color
   */
  inactiveClassName?: string;
}

const roundedVariants = {
  none: '',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

const roundedRightVariants = {
  none: '',
  sm: 'rounded-r-sm',
  md: 'rounded-r-md',
  lg: 'rounded-r-lg',
  full: 'rounded-r-full',
};

const heightVariants = {
  xs: 'h-0.5',
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
  xl: 'h-4',
};

const StepBar = React.forwardRef<HTMLDivElement, StepBarProps>(
  (
    {
      totalSteps,
      currentStep,
      variant = 'steps',
      rounded = 'full',
      activeRoundedRight,
      transparent = false,
      height = 'sm',
      activeClassName,
      inactiveClassName,
      className,
      ...props
    },
    ref,
  ) => {
    // Ensure currentStep is within valid range
    const validCurrentStep = Math.max(1, Math.min(currentStep, totalSteps));
    const progressPercentage = (validCurrentStep / totalSteps) * 100;

    // Get height class
    const heightClass =
      heightVariants[height as keyof typeof heightVariants] || height;

    // Progress bar variant (single continuous bar)
    if (variant === 'progress') {
      const activeBarClassName =
        activeClassName ?? (transparent ? 'bg-white' : 'bg-primary');

      return (
        <div
          ref={ref}
          className={cn(
            'relative w-full overflow-hidden',
            heightClass,
            roundedVariants[rounded],
            transparent ? 'bg-white/20' : 'bg-muted',
            className,
          )}
          role="progressbar"
          aria-valuenow={validCurrentStep}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          {...props}
        >
          <div
            className={cn(
              'h-full transition-all duration-300 ease-out',
              // Apply left rounding from container, right rounding from activeRoundedRight
              activeRoundedRight
                ? roundedRightVariants[activeRoundedRight]
                : roundedVariants[rounded],
              activeBarClassName,
            )}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      );
    }

    // Steps variant (individual separated steps)
    return (
      <div
        ref={ref}
        className={cn('flex gap-2 w-full', className)}
        role="progressbar"
        aria-valuenow={validCurrentStep}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        {...props}
      >
        {Array.from({ length: totalSteps }).map((_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber <= validCurrentStep;
          const isCurrentStep = stepNumber === validCurrentStep;

          return (
            <div
              key={stepNumber}
              className={cn(
                'flex-1 transition-all duration-300',
                heightClass,
                roundedVariants[rounded],
                transparent ? 'bg-transparent' : 'bg-muted',
                {
                  [activeClassName || 'bg-red-400']: isActive && !transparent,
                  [inactiveClassName || 'bg-muted']: !isActive && !transparent,
                  'bg-white/20': !isActive && transparent,
                  'bg-white': isActive && transparent,
                  'scale-105': isCurrentStep,
                },
              )}
              aria-current={isCurrentStep ? 'step' : undefined}
            />
          );
        })}
      </div>
    );
  },
);

StepBar.displayName = 'StepBar';

export { StepBar, type StepBarProps };
